import produce from "immer";
import _ from "lodash";
import { missingIds, reduceById, uniquePropsById } from "redux-saga-tools";
import {
  all,
  call,
  put,
  select,
  takeEvery,
  takeLatest,
} from "redux-saga/effects";
import { customFilterRegistrationFormId } from "../../components/dialog/CustomFilterRegistrationDialog/CustomFilterRegistrationDialog";
import { jobEditFormId } from "../../components/dialog/JobEditDialog/JobEditDialog";
import { onSystemInitializeApplication } from "../../sagas/system.saga";
import { saveAssigns } from "../../services/assigns.service";
import { createJob, deleteJob, updateJob } from "../../services/jobs.service";
import { assignsActions, assignsSelector } from "../../store/assigns.store";
import { masterAssignsSelector } from "../../store/master-assigns.store";
import { systemActions } from "../../store/system.store";
import { uiActions } from "../../store/ui.store";
import { Assign, AssignForm, Job, SaveType } from "../../types/types";
import {
  findById,
  generateUuid,
  isEmptyList,
  replaceItemsById,
} from "../../util/util";
import { assignPageSelectors } from "./assign-page.selector";
import {
  AssignFilterItem,
  assignPageActions,
  AssignPageState,
} from "./assign-page.store";

export function* onSystemInitializeAssignPage() {
  yield put(
    assignPageActions.REDUCER_SET_SELECTED_ASSIGN_ID({ id: undefined })
  );
}

/**
 * システムがアサイン順を更新する
 * @param action
 */
function* onSystemUpdateAssignOrder(
  action: ReturnType<typeof assignPageActions.SYSTEM_UPDATE_ASSIGN_ORDER>
) {
  yield put(
    assignPageActions.REDUCER_SET_ASSIGN_ORDER({
      assignOrder: action.payload.assignOrder,
    })
  );
}

/**
 * ユーザーが年期を変更した
 */
function* onUserChangeNenki(
  action: ReturnType<typeof assignPageActions.USER_CHNAGE_NENKI>
) {
  yield put(
    assignPageActions.REDUCER_SET_NENKI({ nenki: action.payload.nenki })
  );
  yield put(systemActions.SYSTEM_INITIALIZE_APPLICATION());
}

/**
 * ユーザーがアサインを挿入する
 *
 * TODO: ジョブ毎に必ず1行残す
 */
function* onUserInsertAssign() {
  const current = yield select(assignPageSelectors.getSelectedAssign);

  if (!current) {
    return;
  }

  const newItem = createEmptyAssignFrom(current);
  const assigns: Assign[] = yield select(assignsSelector);
  const lastIndex = _.findLastIndex(
    assigns,
    (v) => v.jobCode === current.jobCode
  );
  const modified = produce(assigns, (draft) => {
    draft.splice(lastIndex + 1, 0, newItem);
  });

  yield put(assignsActions.REDUCER_SET_ASSIGNS(modified));
  yield put(
    assignPageActions.REDUCER_SET_SELECTED_ASSIGN_ID({ id: newItem.id })
  );
  // // https://dev.grapecity.co.jp/support/kb/detail.asp?id=81733
  // cv.sourceCollection.push(newItem);
  // cv.itemsAdded.push(newItem);
  // cv.refresh();
  // cv.moveCurrentTo(newItem);
}

/**
 * ユーザーがアサインを編集する
 * @param action
 */
function* onUserEditAssign(
  action: ReturnType<typeof assignPageActions.USER_EDIT_ASSIGN>
) {
  const assigns = yield select(assignsSelector);
  const modified = replaceItemsById(assigns, action.payload.assigns);
  yield put(assignsActions.REDUCER_SET_ASSIGNS(modified));
}

const createEmptyAssignFrom = (from: Assign): Assign => {
  return {
    id: -Number(_.uniqueId()),
    shozokuCode: from.shozokuCode,
    buName: from.buName,
    groupName: from.groupName,
    jobCode: from.jobCode,
    jobName: from.jobName,
    clientName: from.clientName,
    inChargeName: "",
    orderStatusName: from.orderStatusName,
  };
};

const isEmptyAssign = (assign: Assign) =>
  !assign.inChargeName &&
  !assign.memo &&
  !assign.month_1st &&
  !assign.month_2nd &&
  !assign.month_3rd &&
  !assign.month_4th &&
  !assign.month_5th &&
  !assign.month_6th &&
  !assign.process &&
  !assign.skill;

/**
 * ユーザーがアサインを削除する
 */
function* onUserDeleteAssign() {
  const selectedAssignId: number = yield select(
    assignPageSelectors.getSelectedAssignId
  );

  if (!selectedAssignId) {
    return;
  }

  const assigns: Assign[] = yield select(assignsSelector);
  const order: number[] = yield select(assignPageSelectors.getAssignOrder);

  const deleteTargetOrderIndex = order.findIndex((v) => v === selectedAssignId);
  const del = findById(assigns, selectedAssignId);

  if (!del) {
    return;
  }

  const jobCount = assigns.filter((v) => v.jobCode === del.jobCode).length;
  let modified;

  // FIXME: 最後の1件を削除したときの動きがおかしい
  if (jobCount === 1) {
    if (isEmptyAssign(del)) {
      // TODO: グリッドからフォーカスが外れてしまう
      return;
    }

    const emptyItem = createEmptyAssignFrom(del);

    modified = produce(assigns, (draft) => {
      draft.splice(deleteTargetOrderIndex, 1, emptyItem);
    });

    yield put(
      assignPageActions.REDUCER_SET_SELECTED_ASSIGN_ID({ id: emptyItem.id })
    );
  } else {
    modified = produce(assigns, (draft) => {
      _.remove(draft, (v) => v.id === selectedAssignId);
    });

    if (deleteTargetOrderIndex + 1 === order.length) {
      const prevOrderIndex = deleteTargetOrderIndex - 1;
      const prevAssignId = order[prevOrderIndex];
      const prevAssign = findById(assigns, prevAssignId);

      if (prevAssign) {
        yield put(
          assignPageActions.REDUCER_SET_SELECTED_ASSIGN_ID({
            id: prevAssign.id,
          })
        );
      }
    } else {
      const nextOrderIndex = deleteTargetOrderIndex + 1;
      const nextAssignId = order[nextOrderIndex];
      const nextAssign = findById(assigns, nextAssignId);

      if (nextAssign) {
        yield put(
          assignPageActions.REDUCER_SET_SELECTED_ASSIGN_ID({
            id: nextAssign.id,
          })
        );
      }
    }
  }

  // selectedAssignIdは自動でgetSelectedAssignIdで自動再計算されて
  // undefinedになるので、明示的に空にする必要はない
  yield put(assignsActions.REDUCER_SET_ASSIGNS(modified));
}

/**
 * ユーザーがアサイン保存ボタンをクリックした
 */
function* onUserClickedAssignSaveButton() {
  yield put(
    uiActions.SYSTEM_OPEN_CONFIRM_DIALOG({
      title: "アサイン保存確認",
      subText: "アサインを保存します。よろしいですか？",
      primaryAction: assignPageActions.USER_SAVE_ASSIGNS(),
    })
  );
}

/**
 * ユーザーがアサインを保存する
 */
function* onUserSaveAssigns() {
  try {
    yield put(uiActions.REDUCER_LOADING_STARTED());

    const nenki = yield select(assignPageSelectors.getNenki);
    const lhs: Assign[] = yield select(masterAssignsSelector);
    const rhs: Assign[] = yield select(assignsSelector);
    const lhsReduced = reduceById(lhs);
    const rhsReduced = reduceById(rhs);
    const ids = uniquePropsById(lhs.concat(rhs), "id");
    const addedIds = missingIds(lhsReduced, ids);
    const removedIds = missingIds(rhsReduced, ids);

    const itemsNewAdded: Assign[] = rhs.filter((v) =>
      addedIds.includes(v.id.toString())
    );
    const itemsDummyAdded: Assign[] = [];
    lhs
      .filter(
        (v: Assign) =>
          !addedIds.includes(v.id.toString()) &&
          !removedIds.includes(v.id.toString())
      )
      .forEach((v: Assign) => {
        const find = findById(rhs, v.id);
        if (find && find.status === "dummy" && !_.isEqual(v, find)) {
          itemsDummyAdded.push(find);
        }
      });
    const itemsAdded: Assign[] = itemsNewAdded.concat(itemsDummyAdded);

    const itemsRemoved: Assign[] = lhs.filter((v) =>
      removedIds.includes(v.id.toString())
    );

    const itemsEdited: Assign[] = [];
    lhs
      .filter(
        (v: Assign) =>
          !addedIds.includes(v.id.toString()) &&
          !removedIds.includes(v.id.toString())
      )
      .forEach((v: Assign) => {
        const find = findById(rhs, v.id);
        if (find && find.status !== "dummy" && !_.isEqual(v, find)) {
          itemsEdited.push(find);
        }
      });

    console.log("itemsAdded", itemsAdded);
    console.log("itemsEdited", itemsEdited);
    console.log("itemsRemoved", itemsRemoved);

    const mapper = (plan: Assign, st: SaveType): AssignForm => {
      return {
        id: plan.id,
        // halfPeriod: plan.halfPeriod, TODO: FIXME
        halfPeriod: nenki,
        updatedAt: new Date(),
        shozokuCode: plan.shozokuCode,
        jobCode: plan.jobCode,
        process: plan.process,
        skill: plan.skill,
        inChargeKojinCode: plan.inChargeKojinCode,
        month_1st: plan.month_1st ? plan.month_1st : 0,
        month_2nd: plan.month_2nd ? plan.month_2nd : 0,
        month_3rd: plan.month_3rd ? plan.month_3rd : 0,
        month_4th: plan.month_4th ? plan.month_4th : 0,
        month_5th: plan.month_5th ? plan.month_5th : 0,
        month_6th: plan.month_6th ? plan.month_6th : 0,
        memo: plan.memo,
        saveType: st,
      };
    };

    const added = itemsAdded.map((item) => mapper(item, "NEW"));
    const edited = itemsEdited.map((item) => mapper(item, "UPD"));
    const removed = itemsRemoved.map((item) => mapper(item, "DEL"));

    const saveItems = added.concat(edited).concat(removed);
    console.log("saveItems", saveItems);
    yield call(saveAssigns, saveItems);

    const selectedFilterId = yield select(
      assignPageSelectors.getSelectedFilterId
    );
    if (selectedFilterId === "unsaved_changes") {
      yield put(
        assignPageActions.REDUCER_SET_ASSIGN_FILTER({ selectedFilterId: "all" })
      );
    }

    yield put(
      assignPageActions.USER_RELOAD_ASSIGNS_LIST({
        message: "アサインを保存しました",
      })
    );
  } finally {
    yield put(uiActions.REDUCER_LOADING_STOPPED());
  }
}

function* onUserSelectAssign(
  action: ReturnType<typeof assignPageActions.USER_SELECT_ASSIGN>
) {
  yield put(
    assignPageActions.REDUCER_SET_SELECTED_ASSIGN_ID({ id: action.payload.id })
  );
}

/**
 * ユーザーがグリッドの状態を変更する
 * @param action
 */
function* onUserChangeGridState(
  action: ReturnType<typeof assignPageActions.USER_CHANGE_GRID_STATE>
) {
  yield put(
    assignPageActions.REDUCER_SET_GRID_STATE({
      gridState: action.payload.gridState,
    })
  );
}

/**
 * ユーザーがアサインリストを再読み込みする
 */
function* onUserReloadAssignsList(
  action: ReturnType<typeof assignPageActions.USER_RELOAD_ASSIGNS_LIST>
) {
  yield put(assignsActions.REDUCER_SET_ASSIGNS([]));
  yield call(onSystemInitializeApplication);

  if (action.payload.message) {
    yield put(
      uiActions.REDUCER_SHOW_MESSAGE_BAR({ message: action.payload.message })
    );
  }
}

/**
 * ユーザーがアサインリストをリセットする
 */
function* onUserResetAssignsList() {
  yield put(assignPageActions.REDUCER_SET_GRID_STATE({ gridState: undefined }));
  yield put(assignPageActions.USER_RELOAD_ASSIGNS_LIST({}));
}

/**
 * ユーザーがフィルタを変更する
 * @param action
 */
function* onUserChangeAssignFilter(
  action: ReturnType<typeof assignPageActions.USER_CHANGE_ASSIGN_FILTER>
) {
  const nextId = action.payload.selectedFilterId;
  const currentId = yield select(assignPageSelectors.getSelectedFilterId);

  if (nextId === currentId) {
    return;
  }

  const nextFilter: AssignFilterItem = yield select(
    assignPageSelectors.getFilterById,
    nextId
  );

  const assignPageRootState: AssignPageState = yield select(
    assignPageSelectors.getRootState
  );
  const nextState = produce(assignPageRootState, (draft) => {
    draft.selectedGroupingCondition = nextFilter.defaultGroupingKey;
    draft.defaultGroupingExpansionState =
      nextFilter.defaultGroupingExpansionState;
    draft.selectedFilterId = nextId;
    draft.selectedAssignId = undefined;
  });

  yield put(
    assignPageActions.REDUCER_SET_ASSIGN_PAGE_ROOT_STATE({ state: nextState })
  );

  // 選択されたフィルタに設定されているグルーピング条件を適用
  // yield put(
  //   assignPageActions.REDUCER_SET_SELECTED_GROUPING_CONDITION({
  //     groupingKey: nextFilter.defaultGroupingKey
  //   })
  // );

  // yield put(
  //   assignPageActions.REDUCER_SET_ASSIGN_FILTER({
  //     selectedFilterId: nextId
  //   })
  // );

  // yield put(
  //   assignPageActions.REDUCER_SET_SELECTED_ASSIGN_ID({
  //     id: undefined
  //   })
  // );

  // 選択されたフィルタに設定されているデフォルトグルーピング展開状態を適用
  // yield put(
  //   assignPageActions.REDUCER_SET_DEFAULT_GROUPING_EXPANSION_STATE({
  //     defaultGroupingExpansionState: nextFilter.defaultGroupingExpansionState
  //   })
  // );
}

/**
 * ユーザーがジョブ登録ボタンをクリックした
 */
function* onUserClickedJobCreateButton() {
  yield put(
    uiActions.SYSTEM_OPEN_DIALOG({
      type: "JOB_CREATE",
      title: "ジョブ登録",
      isModal: true,
    })
  );
}

/**
 * ユーザーがジョブ更新ボタンをクリックした
 */
function* onUserClickedJobUpdateButton() {
  yield put(
    uiActions.SYSTEM_OPEN_DIALOG({
      type: "JOB_UPDATE",
      title: "ジョブ編集",
      isModal: true,
    })
  );
}

/**
 * ユーザーが、ジョブ削除ボタンをクリックした
 */
function* onUserClickedJobDeleteButton() {
  yield put(
    uiActions.SYSTEM_OPEN_CONFIRM_DIALOG({
      title: "ジョブ削除確認",
      subText:
        "選択されているジョブとそれに紐付くアサインをすべて削除します。よろしいですか？",
      primaryAction: assignPageActions.SYSTEM_DELETE_SELECTED_JOB(),
    })
  );
}

/**
 * システムが、選択されているジョブを削除する
 */
function* onSystemDeleteSelectedJob() {
  const job: Job | undefined = yield select(assignPageSelectors.getSelectedJob);

  if (job) {
    yield call(deleteJob, job.id);
    yield put(
      assignPageActions.USER_RELOAD_ASSIGNS_LIST({
        message: "ジョブを削除しました",
      })
    );
  } else {
    console.log("削除するジョブを選択してください");
  }
}

/**
 * ユーザーが新規ジョブ登録ボタンをクリックした
 * @param action
 * FIXME: ネーミング
 */
function* onUserClickedJobRegisterButton(
  action: ReturnType<typeof assignPageActions.USER_CLICKED_JOB_REGISTER_BUTTON>
) {
  const { jobForm } = action.payload;

  if (jobForm.id) {
    yield call(updateJob, action.payload.jobForm);
    yield put(uiActions.SYSTEM_CLOSE_DIALOG({ formId: jobEditFormId }));
    yield put(
      assignPageActions.USER_RELOAD_ASSIGNS_LIST({
        message: "ジョブを更新しました",
      })
    );
    return;
  } else {
    yield call(createJob, action.payload.jobForm);
    // const res: CreatedResponse = yield call(createJob, action.payload.jobForm);
    yield put(uiActions.SYSTEM_CLOSE_DIALOG({ formId: jobEditFormId }));
    yield put(
      assignPageActions.USER_RELOAD_ASSIGNS_LIST({
        message: "ジョブを登録しました",
      })
    );
    // TODO: 登録したジョブを選択する
    // const assigns: Assign[] = yield select(assignsSelector);
    // const assign = assigns.find((v: Assign) => v.jobId === res.id);
    // if (assign) {
    //   yield put(
    //     assignPageActions.REDUCER_SET_SELECTED_ASSIGN_ID({ id: assign.id })
    //   );
    // }
    return;
  }
}

/**
 * ユーザーがテキスト検索条件を変更した
 * @param action
 */
function* onUserChangeTextSearchCondition(
  action: ReturnType<typeof assignPageActions.USER_CHANGE_TEXT_SEARCH_CONDITION>
) {
  const conditions = action.payload;
  yield put(assignPageActions.REDUCER_SET_TEXT_SEARCH_CONDITIONS(conditions));
}

/**
 * ユーザーがカスタムフィルター登録ボタンをクリックした
 */
function* onUserClickedCustomFilterRegisterButton() {
  yield put(
    uiActions.SYSTEM_OPEN_DIALOG({
      type: "CUSTOM_FILTER_REGISTRATION",
      title: "カスタムフィルター登録",
      isModal: true,
    })
  );
}

/**
 * ユーザーがカスタムフィルターSubmitボタンをクリックした
 * @param action
 */
function* onUserClickedSubmitCustomFilterButton(
  action: ReturnType<
    typeof assignPageActions.USER_CLICKED_SUBMIT_CUSTOM_FILTER_BUTTON
  >
) {
  const filterName = action.payload.filterName;
  const defaultGroupingKey = action.payload.defaultGroupingKey;
  const defaultGroupingExpansionState =
    action.payload.defaultGroupingExpansionState;
  const conditions = yield select(assignPageSelectors.getTextSearchConditions);
  const filter: AssignFilterItem = {
    id: generateUuid(),
    label: filterName,
    icon: "FavoriteStar",
    defaultGroupingKey,
    defaultGroupingExpansionState,
    customFilterCondition: conditions,
  };
  const currentFilters = yield select(assignPageSelectors.getCustomFilters);
  const newFilters = currentFilters.concat(filter);
  yield put(assignPageActions.RECUCER_SET_CUSTOM_FILTERS(newFilters));
  yield put(
    uiActions.SYSTEM_CLOSE_DIALOG({ formId: customFilterRegistrationFormId })
  );
  yield put(
    uiActions.SYSTEM_SHOW_MESSAGE_BAR({
      message: "カスタムフィルターを登録しました",
    })
  );
}

/**
 * ユーザーがグルーピング条件を選択した
 * @param action
 */
function* onUserSelectGroupingCondition(
  action: ReturnType<typeof assignPageActions.USER_SELECT_GROUPING_CONDITION>
) {
  const key = action.payload.groupingKey;
  yield put(
    assignPageActions.REDUCER_SET_SELECTED_GROUPING_CONDITION({
      groupingKey: key,
    })
  );
}

/**
 * ユーザーがフィルタ管理ボタンをクリックした
 */
function* onUserClickedFilterManagementButton() {
  yield put(
    uiActions.SYSTEM_OPEN_DIALOG({
      type: "CUSTOM_FILTER_MANAGEMENT",
      title: "カスタムフィルター管理",
      isModal: true,
    })
  );
}

/**
 * ユーザーがカスタムフィルターを並び替えた
 * @param action
 */
function* onUserReorderCustomFilters(
  action: ReturnType<typeof assignPageActions.USER_REORDER_CUSTOM_FILTERS>
) {
  const filters = action.payload.filters;
  yield put(assignPageActions.RECUCER_SET_CUSTOM_FILTERS(filters));
}

/**
 * ユーザーがカスタムフィルター保存ボタンをクリックした
 * @param action
 */
function* onUserClickedSaveCustomFilterButton(
  action: ReturnType<
    typeof assignPageActions.USER_CLICKED_SAVE_CUSTOM_FILTER_BUTTON
  >
) {
  const filters = action.payload.filters;
  yield put(assignPageActions.RECUCER_SET_CUSTOM_FILTERS(filters));
  yield put(
    uiActions.SYSTEM_CLOSE_DIALOG({ formId: customFilterRegistrationFormId })
  );
  yield put(
    uiActions.SYSTEM_SHOW_MESSAGE_BAR({
      message: "カスタムフィルターを保存しました",
    })
  );
}

/**
 * ユーザーがカスタムフィルター削除ボタンをクリックした
 * @param action
 */
function* onUserClickedDeleteCustomFilterButton(
  action: ReturnType<
    typeof assignPageActions.USER_CLICKED_DELETE_CUSTOM_FILTER_BUTTON
  >
) {
  const filterId = action.payload.filterId;

  yield put(
    uiActions.SYSTEM_OPEN_CONFIRM_DIALOG({
      title: "カスタムフィルター削除確認",
      subText: "カスタムフィルターを削除します。よろしいですか？",
      primaryAction: assignPageActions.SYSTEM_DELETE_CUSTOM_FILTER({
        filterId,
      }),
    })
  );
}

/**
 * システムがカスタムフィルターを削除する
 * @param action
 */
function* onSystemDeleteCustomFilter(
  action: ReturnType<
    typeof assignPageActions.USER_CLICKED_DELETE_CUSTOM_FILTER_BUTTON
  >
) {
  const filterId = action.payload.filterId;
  const filters: AssignFilterItem[] = yield select(
    assignPageSelectors.getCustomFilters
  );

  const removed = produce(filters, (draft) => {
    _.remove(draft, (v) => v.id === filterId);
  });
  yield put(assignPageActions.RECUCER_SET_CUSTOM_FILTERS(removed));

  const selectedFilterId = yield select(
    assignPageSelectors.getSelectedFilterId
  );
  if (isEmptyList(filters) || filterId === selectedFilterId) {
    yield put(
      assignPageActions.REDUCER_SET_ASSIGN_FILTER({
        selectedFilterId: "all",
      })
    );
  }
}

/**
 * ユーザーが変更履歴ボタンをクリックした
 */
function* onUserClickedOpenChangeHistoryButton() {
  yield put(
    uiActions.SYSTEM_OPEN_DIALOG({
      type: "CHANGE_HISTORY",
      title: "変更履歴",
      isModal: false,
    })
  );
}

/**
 * アサイン画面Saga
 */
export function* assignPageSaga() {
  yield all([
    yield takeLatest(
      assignPageActions.SYSTEM_UPDATE_ASSIGN_ORDER,
      onSystemUpdateAssignOrder
    ),
    yield takeLatest(
      assignPageActions.SYSTEM_DELETE_SELECTED_JOB,
      onSystemDeleteSelectedJob
    ),
    yield takeLatest(
      assignPageActions.SYSTEM_DELETE_CUSTOM_FILTER,
      onSystemDeleteCustomFilter
    ),
    yield takeLatest(assignPageActions.USER_CHNAGE_NENKI, onUserChangeNenki),
    yield takeEvery(assignPageActions.USER_INSERT_ASSIGN, onUserInsertAssign),
    yield takeLatest(assignPageActions.USER_EDIT_ASSIGN, onUserEditAssign),
    yield takeEvery(assignPageActions.USER_DELETE_ASSIGN, onUserDeleteAssign),
    yield takeLatest(
      assignPageActions.USER_CLICKED_ASSIGN_SAVE_BUTTON,
      onUserClickedAssignSaveButton
    ),
    yield takeEvery(assignPageActions.USER_SAVE_ASSIGNS, onUserSaveAssigns),
    yield takeLatest(assignPageActions.USER_SELECT_ASSIGN, onUserSelectAssign),
    yield takeEvery(
      assignPageActions.USER_CHANGE_GRID_STATE,
      onUserChangeGridState
    ),
    yield takeLatest(
      assignPageActions.USER_RELOAD_ASSIGNS_LIST,
      onUserReloadAssignsList
    ),
    yield takeLatest(
      assignPageActions.USER_RESET_ASSIGNS_LIST,
      onUserResetAssignsList
    ),
    yield takeLatest(
      assignPageActions.USER_CHANGE_ASSIGN_FILTER,
      onUserChangeAssignFilter
    ),
    yield takeEvery(
      assignPageActions.USER_CLICKED_JOB_CREATE_BUTTON,
      onUserClickedJobCreateButton
    ),
    yield takeEvery(
      assignPageActions.USER_CLICKED_JOB_UPDATE_BUTTON,
      onUserClickedJobUpdateButton
    ),
    yield takeEvery(
      assignPageActions.USER_CLICKED_JOB_DELETE_BUTTON,
      onUserClickedJobDeleteButton
    ),
    yield takeEvery(
      assignPageActions.USER_CLICKED_JOB_REGISTER_BUTTON,
      onUserClickedJobRegisterButton
    ),
    yield takeLatest(
      assignPageActions.USER_CHANGE_TEXT_SEARCH_CONDITION,
      onUserChangeTextSearchCondition
    ),
    yield takeLatest(
      assignPageActions.USER_CLICKED_CUSTOM_FILTER_REGISTER_BUTTON,
      onUserClickedCustomFilterRegisterButton
    ),
    yield takeLatest(
      assignPageActions.USER_CLICKED_SUBMIT_CUSTOM_FILTER_BUTTON,
      onUserClickedSubmitCustomFilterButton
    ),
    yield takeLatest(
      assignPageActions.USER_SELECT_GROUPING_CONDITION,
      onUserSelectGroupingCondition
    ),
    yield takeLatest(
      assignPageActions.USER_CLICKED_FILTER_MANAGEMENT_BUTTON,
      onUserClickedFilterManagementButton
    ),
    yield takeLatest(
      assignPageActions.USER_REORDER_CUSTOM_FILTERS,
      onUserReorderCustomFilters
    ),
    yield takeLatest(
      assignPageActions.USER_CLICKED_SAVE_CUSTOM_FILTER_BUTTON,
      onUserClickedSaveCustomFilterButton
    ),
    yield takeLatest(
      assignPageActions.USER_CLICKED_DELETE_CUSTOM_FILTER_BUTTON,
      onUserClickedDeleteCustomFilterButton
    ),
    yield takeLatest(
      assignPageActions.USER_CLICKED_OPEN_CHANGE_HISTORY_BUTTON,
      onUserClickedOpenChangeHistoryButton
    ),
  ]);
}
